import { useState, useEffect, useRef } from "react";

/* ─── Estilos y Tokens ─────────────────────────────────────────────────── */
import { P, FF } from "./styles/tokens";
import GlobalStyle from "./styles/GlobalStyle";

/* ─── Onboarding ───────────────────────────────────────────────────────── */
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import AppTour from "./components/onboarding/AppTour";
import PlanWelcomeModal from "./components/onboarding/PlanWelcomeModal";

/* ─── Componentes UI ───────────────────────────────────────────────────── */
import { ArboLogo } from "./components/ui";
import TrialBanner from "./components/ui/TrialBanner";

/* ─── Navegación ───────────────────────────────────────────────────────── */
import { MonthPicker } from "./components/navigation";

/* ─── Duky ─────────────────────────────────────────────────────────────── */
import { DukyPanel, DukyFab } from "./components/duky";

/* ─── Audio ────────────────────────────────────────────────────────────── */
import { AudioRecorder } from "./components/inputs";
import { api } from "./services/api";

/* ─── Layouts ──────────────────────────────────────────────────────────── */
import DesktopLayout from "./layouts/DesktopLayout";
import MobileLayout from "./layouts/MobileLayout";

/* ─── Contexts ─────────────────────────────────────────────────────────── */
import { useNavigation } from "./contexts/NavigationContext";
import { useFinancial } from "./contexts/FinancialContext";
import { useUI } from "./contexts/UIContext";
import { useToast } from "./contexts/ToastContext";

/* ─── Auth ─────────────────────────────────────────────────────────────── */
import { useAuth } from "./contexts/AuthContext";
import { LoginPage, AuthCallback } from "./components/auth";
import { LandingPage } from "./components/landing";

/* ─── Suscripción ──────────────────────────────────────────────────────── */
import { useSubscription } from "./hooks/useSubscription";

/* ═══════════════════════════════════════════════════════════════════════════
   APP — Gate de autenticacion
   ═══════════════════════════════════════════════════════════════════════════ */
export default function App() {
  const { user, loading: authLoading } = useAuth();

  // Manejar callback de OAuth (Google redirect)
  if (window.location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }

  // Loading mientras se verifica la sesion
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: P.bg }}>
        <ArboLogo />
      </div>
    );
  }

  // Si no hay usuario autenticado, mostrar landing page
  if (!user) return <LandingPage />;

  // Usuario autenticado — renderizar la app
  return <AuthenticatedApp />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   AUTHENTICATED APP — orquestador delgado
   ═══════════════════════════════════════════════════════════════════════════ */
function AuthenticatedApp() {
  const { mobile, dukyOpen, setDukyOpen, audioOpen, setAudioOpen } = useUI();
  const { activeMonth, setActiveMonth, showMonthPicker, setShowMonthPicker, view, setView, setSection } = useNavigation();
  const {
    loading, month, setMonth, totalIng, totalFij, totalExt,
    ahorroReal, metas, presupuestos, data, refetchGlobal, refetchMonths,
    onboardingCompleted, setOnboardingCompleted,
    createCuenta, setRecurrentes, recurrentes, cuentas, mesId,
  } = useFinancial();
  const { show: showToast } = useToast();
  const { refetch: refetchSub } = useSubscription();

  // Modal de bienvenida post-signup (?plan=basico|pro|gratis)
  const [welcomePlan, setWelcomePlan] = useState(null);
  const queryHandled = useRef(false);

  // Escuchar eventos de límite de plan (disparados desde api.js al recibir 403)
  useEffect(() => {
    const handler = (e) => {
      const msg = typeof e.detail === "string" ? e.detail : "Límite de tu plan alcanzado.";
      showToast(`${msg} — Ver planes`);
      setView("config");
    };
    window.addEventListener("plan-limit-reached", handler);
    return () => window.removeEventListener("plan-limit-reached", handler);
  }, [showToast, setView]);

  // Detectar query params post-OAuth (?plan=) y post-Checkout (?upgrade=)
  // Una sola vez por mount — el flag evita que se vuelva a disparar al re-renderizar
  useEffect(() => {
    if (queryHandled.current) return;
    queryHandled.current = true;

    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan");
    const upgrade = params.get("upgrade");

    if (plan && ["basico", "pro", "gratis"].includes(plan)) {
      setWelcomePlan(plan);
    }

    if (upgrade === "success") {
      showToast("¡Bienvenido a tu plan! 🎉");
      // El webhook puede tardar ~2s en propagar — refetch dos veces
      refetchSub();
      setTimeout(() => refetchSub(), 2500);
    } else if (upgrade === "canceled") {
      showToast("Pago cancelado. Tu plan no cambió.");
    }

    // Limpiar query params de la URL para que no se re-disparen al recargar
    if (plan || upgrade) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [showToast, refetchSub]);

  const [showTour, setShowTour] = useState(() => {
    try { return localStorage.getItem("arbo_tour_done") !== "true"; } catch { return true; }
  });

  /* ── Loading ── */
  if (loading) return (
    <>
      <GlobalStyle />
      <div style={{ minHeight: "100vh", background: P.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <ArboLogo size={48} showText animated />
        <div style={{ fontSize: 14, color: P.textSm, fontFamily: FF }}>Cargando tus finanzas...</div>
      </div>
    </>
  );

  /* ── Onboarding Gate ── */
  if (!onboardingCompleted) return (
    <>
      <GlobalStyle />
      <OnboardingFlow
        onComplete={() => {
          setOnboardingCompleted(true);
          setShowTour(true);
          refetchGlobal();
          // El gasto y el recurrente del onboarding se crearon directo en el
          // backend; hay que traer el mes de la fuente para que la app no
          // arranque con datos viejos.
          refetchMonths();
        }}
        createCuenta={createCuenta}
        setRecurrentes={setRecurrentes}
        recurrentes={recurrentes}
        setMonth={setMonth}
        cuentas={cuentas}
        mobile={mobile}
      />
    </>
  );

  /* ── Root Render ── */
  return (
    <>
      <GlobalStyle />
      <div style={{ minHeight: "100vh", background: P.bg, color: P.text, fontFamily: FF }}>
        <TrialBanner />
        {mobile ? <MobileLayout /> : <DesktopLayout />}
      </div>

      {/* Modal de bienvenida post-signup (solo se muestra si vino con ?plan=) */}
      {welcomePlan && (
        <PlanWelcomeModal
          plan={welcomePlan}
          onClose={() => {
            setWelcomePlan(null);
            refetchSub();
          }}
        />
      )}

      {/* Tour contextual */}
      {showTour && mobile && <AppTour onComplete={() => setShowTour(false)} />}

      {/* Duky Panel */}
      {!dukyOpen && !mobile && <DukyFab onClick={() => setDukyOpen(true)} />}
      <DukyPanel open={dukyOpen} onClose={() => setDukyOpen(false)}
        month={month} mesId={mesId} totalIng={totalIng} totalFij={totalFij} totalExt={totalExt}
        ahorroReal={ahorroReal} activeMonth={activeMonth} metas={metas}
        presupuestos={presupuestos} mobile={mobile}
        onNavigate={(sec) => { setView("mes"); setSection(sec); }} />
      {showMonthPicker && <MonthPicker activeMonth={activeMonth} onSelect={setActiveMonth} onClose={() => setShowMonthPicker(false)} data={data} />}

      {/* Audio Recorder — global overlay */}
      {audioOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <AudioRecorder
            mobile={mobile}
            onClose={() => setAudioOpen(false)}
            onConfirm={(transacciones) => {
              const newIngresos = [];
              const newFijos = [];
              const newExtras = [];
              const hoy = new Date().toISOString().slice(0, 10);
              transacciones.forEach(tx => {
                const fechaTx = tx.fecha || hoy;
                if (tx.tipo === "transferencia" && tx.cuenta_origen_id && tx.cuenta_destino_id) {
                  api.transferencias.create({
                    cuenta_origen: tx.cuenta_origen_id,
                    cuenta_destino: tx.cuenta_destino_id,
                    monto: tx.monto,
                    concepto: tx.concepto || "Transferencia por voz",
                  }).then(() => refetchGlobal()).catch(e => console.error("Transfer error:", e));
                } else if (tx.tipo === "ingreso") {
                  newIngresos.push({ id: crypto.randomUUID(), concepto: tx.concepto, monto: tx.monto, cuentaId: tx.cuenta_id || null, fecha: fechaTx });
                } else if (tx.tipo === "gasto_fijo") {
                  newFijos.push({ id: crypto.randomUUID(), concepto: tx.concepto, monto: tx.monto, cat: "", fechaLimite: "", pagado: false, montoPagado: null, cuentaId: tx.cuenta_id || null });
                } else {
                  newExtras.push({ id: crypto.randomUUID(), concepto: tx.concepto, monto: tx.monto, cat: tx.categoria || "Otros", msi: null, cuentaId: tx.cuenta_id || null, fecha: fechaTx });
                }
              });
              if (newIngresos.length || newFijos.length || newExtras.length) {
                setMonth(prev => ({
                  ...prev,
                  ingresos: [...prev.ingresos, ...newIngresos],
                  fijos: [...prev.fijos, ...newFijos],
                  extras: [...prev.extras, ...newExtras],
                }));
              }
            }}
          />
        </div>
      )}
    </>
  );
}
